import { Box, Button, CircularProgress, Dialog, IconButton, Stack, Tooltip, Typography } from '@mui/material'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck'
import EditIcon from '@mui/icons-material/Edit'
import { useEffect, useState } from 'react'
import { apiGet } from '../../generic/Api_Functions'
import { useSmartTranslation } from '../../generic/hooks/useSmartTranslation'
import AlertUI from '../../generic/AlertUI'
import MosInfoProvider from '../Widgets/common/provider/MosInfoProvider'
import WidgetGrid from '../Widgets/base/WidgetGrid'
import { parseTemplateWidgets } from './widgetTemplateParse'

const noop = () => {}
const HEADER_HEIGHT = '3.5rem'

/**
 * Read-only, full-screen preview of a layout, opened from the Assets
 * "Change widget template" list before applying. Renders the widgets in
 * a static grid (no toolbar/editing) so the arrangement and widget types are
 * visible; widgets show placeholder state (no device context). Offers "Apply"
 * and "Edit and apply" shortcuts that defer to the parent. Defaults keep the
 * device-template behaviour; initial-screen templates pass
 * endpointBase='widgets/initialScreenTemplates' and applyEnabled (which
 * overrides the device check on the Apply button). Non-template layouts (e.g. a
 * user's current initial screen) pass loadPath (a GET returning a plain widget
 * list), a subtitle replacing the template name, and custom header actions.
 */
const PreviewTemplateDialog = ({ open, template, device, onClose, onApply, onEdit, endpointBase = 'widgets/templates', applyEnabled, loadPath = '', subtitle = '', actions = null }) => {
  const { t_ } = useSmartTranslation()
  const [AlertElement, showAlert] = AlertUI()
  const [widgets, setWidgets] = useState([])
  const [loading, setLoading] = useState(false)

  const path = loadPath || (template?.id ? endpointBase + '/' + template.id : '')

  useEffect(() => {
    if (!open || !path) return
    let cancelled = false
    const load = async () => {
      setLoading(true)
      try {
        const full = await apiGet(path)
        if (!cancelled) setWidgets(parseTemplateWidgets(Array.isArray(full) ? full : full?.widgets))
      } catch (e) {
        if (!cancelled) showAlert({ title: t_('error loading widget template'), message: t_(e.message || 'Unknown error'), severity: 'error' })
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => { cancelled = true }
  }, [open, path, showAlert, t_])

  return (
    <Dialog fullScreen open={open} onClose={() => onClose()} PaperProps={{ sx: { overflow: 'hidden' } }}>
      <Stack direction='row' spacing={2} alignItems='center' sx={{ px: 2, height: HEADER_HEIGHT, flexShrink: 0, boxSizing: 'border-box', borderBottom: 1, borderColor: 'divider' }}>
        <Tooltip title={t_('Back')}>
          <IconButton onClick={() => onClose()} aria-label={t_('Back')}><ArrowBackIcon /></IconButton>
        </Tooltip>
        <Typography variant='h6' sx={{ whiteSpace: 'nowrap' }}>{t_('Preview')}</Typography>
        <Typography color='text.secondary' noWrap sx={{ flexGrow: 1 }}>{subtitle || template?.name}</Typography>
        {actions}
        {onEdit && (
          <Button variant='text' startIcon={<EditIcon />} onClick={() => onEdit(template)}>
            {t_('Edit and apply')}
          </Button>
        )}
        {onApply && (
          <Button variant='contained' startIcon={<PlaylistAddCheckIcon />} onClick={() => onApply(template)} disabled={applyEnabled === undefined ? !device : !applyEnabled}>
            {t_('Apply')}
          </Button>
        )}
      </Stack>
      <Box sx={{ height: 'calc(100vh - ' + HEADER_HEIGHT + ')', overflow: 'auto' }}>
        <Stack direction='column' sx={{ height: '100%' }}>
          <Box sx={{ flexGrow: 1 }}>
            <div style={{ position: 'sticky', top: '0.5rem', zIndex: 100, padding: '0 1rem' }}>{AlertElement}</div>
            {loading
              ? <Stack justifyContent='center' alignItems='center' sx={{ height: '100%' }}><CircularProgress size='6rem' /></Stack>
              : (
                <MosInfoProvider>
                  <WidgetGrid
                    widgets={widgets}
                    setWidgets={setWidgets}
                    reloadWidgets={noop}
                    onSave={noop}
                    editing={false}
                    setEditing={noop}
                    preview
                  />
                </MosInfoProvider>
                )}
          </Box>
        </Stack>
      </Box>
    </Dialog>
  )
}

export default PreviewTemplateDialog
